import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import {
  Trash2,
  Clock,
  Settings2,
  CalendarCheck,
  Globe,
  ShieldCheck,
  BadgeCheck,
  Loader2,
  Check,
  KeyRound,
  Timer,
} from 'lucide-react'

import { Input } from '@components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select'
import { useAuthStore } from '@hooks/useAuthStore'
import { useToast } from '@hooks/use-toast'
import { appointmentApi } from '@services/appointment.service'
import { authApi } from '@services/auth.service'
import { LanguageSelect } from '@components/settings/LanguageSelect'
import { SpecialtyThemePicker } from '@components/settings/SpecialtyThemePicker'
import { DoctorAvailabilityOverride, DoctorWeeklyAvailability } from '@/types'
import { cn } from '@lib/utils'

const timezoneOptions = [
  { value: 'UTC', label: 'UTC', country: 'Coordinated Universal Time' },
  { value: 'America/New_York', label: 'UTC-5', country: 'United States (East)' },
  { value: 'America/Chicago', label: 'UTC-6', country: 'United States (Central)' },
  { value: 'America/Los_Angeles', label: 'UTC-8', country: 'United States (West)' },
  { value: 'America/Mexico_City', label: 'UTC-6', country: 'Mexico' },
  { value: 'America/Argentina/Buenos_Aires', label: 'UTC-3', country: 'Argentina' },
  { value: 'Europe/London', label: 'UTC+0', country: 'United Kingdom' },
  { value: 'Europe/Paris', label: 'UTC+1', country: 'France' },
  { value: 'Europe/Madrid', label: 'UTC+1', country: 'Spain' },
  { value: 'Asia/Tokyo', label: 'UTC+9', country: 'Japan' },
]

const weekdayOptions = [
  { value: 0, label: 'Lunes', short: 'Lu' },
  { value: 1, label: 'Martes', short: 'Ma' },
  { value: 2, label: 'Miércoles', short: 'Mi' },
  { value: 3, label: 'Jueves', short: 'Ju' },
  { value: 4, label: 'Viernes', short: 'Vi' },
  { value: 5, label: 'Sábado', short: 'Sa' },
  { value: 6, label: 'Domingo', short: 'Do' },
]


// Compact section header used consistently across cards
function SectionHeader({ icon: Icon, title, badge }: { icon: React.ElementType; title: string; badge?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-ml-primary/10">
        <Icon className="h-4 w-4 text-ml-primary" />
      </div>
      <h3 className="text-base font-headline italic text-ml-primary flex-1">{title}</h3>
      {badge}
    </div>
  )
}

// Preference row with icon, label, description and a right-side control
function PrefRow({
  icon: Icon,
  label,
  description,
  control,
}: {
  icon: React.ElementType
  label: string
  description: string
  control: React.ReactNode
}) {
  return (
    <div className="flex items-center gap-4 py-4">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-ml-surface-container-lowest">
        <Icon className="h-3.5 w-3.5 text-ml-on-surface-variant/60" />
      </div>
      <div className="flex-1 min-w-0 pr-2">
        <p className="text-sm font-semibold text-ml-on-surface leading-tight">{label}</p>
        <p className="text-xs text-ml-on-surface-variant/50 mt-0.5 leading-relaxed">{description}</p>
      </div>
      <div className="shrink-0">{control}</div>
    </div>
  )
}

// Metric input — a numeric field with an embedded unit badge
function MetricInput({
  label,
  value,
  unit,
  onChange,
  onBlur,
}: {
  label: string
  value: number
  unit: string
  onChange: (n: number) => void
  onBlur?: () => void
}) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/50">{label}</label>
      <div className="flex items-center gap-0 rounded-lg border border-ml-outline-variant/25 bg-ml-surface-container-lowest overflow-hidden focus-within:border-ml-primary/40 focus-within:ring-1 focus-within:ring-ml-primary/20 transition-all">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
          onBlur={onBlur}
          className="flex-1 bg-transparent px-3 py-2.5 text-sm text-ml-on-surface font-body focus:outline-none min-w-0"
        />
        <span className="px-3 py-2.5 text-xs font-bold text-ml-on-surface-variant/40 bg-ml-outline-variant/5 border-l border-ml-outline-variant/20 whitespace-nowrap">
          {unit}
        </span>
      </div>
    </div>
  )
}

// Inline auto-save status — replaces an explicit Save button for fields that persist on blur/change
type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

function SaveStatusIndicator({ status }: { status: SaveStatus }) {
  const { t } = useTranslation()
  if (status === 'saving') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-ml-on-surface-variant/50">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {t('settings.saving')}
      </span>
    )
  }
  if (status === 'saved') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-ml-primary font-medium">
        <Check className="h-3.5 w-3.5" />
        {t('settings.saved')}
      </span>
    )
  }
  if (status === 'error') {
    return <span className="text-xs text-ml-error font-medium">{t('settings.save_error')}</span>
  }
  return null
}

// Fades a 'saved'/'error' status back to 'idle' after a short delay
function useAutoResetStatus(status: SaveStatus, setStatus: (s: SaveStatus) => void, delay = 2000) {
  useEffect(() => {
    if (status !== 'saved' && status !== 'error') return
    const id = setTimeout(() => setStatus('idle'), delay)
    return () => clearTimeout(id)
  }, [status, setStatus, delay])
}

export function SettingsPage() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const currentUser = useAuthStore((state) => state.user)
  const updateUser = useAuthStore((state) => state.updateUser)
  const canManageAvailability = true
  const doctorId = currentUser?.id || ''
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false)

  const [ruleForm, setRuleForm] = useState({
    timezone: 'UTC',
    slot_minutes: 30,
    lead_time_minutes: 120,
    max_horizon_days: 60,
    is_active: true,
  })
  const [ruleSaveStatus, setRuleSaveStatus] = useState<SaveStatus>('idle')
  const lastSavedRule = useRef(ruleForm)
  useAutoResetStatus(ruleSaveStatus, setRuleSaveStatus)

  const [weeklyForm, setWeeklyForm] = useState({
    weekday: 1,
    start_time: '09:00',
    end_time: '17:00',
    is_active: true,
  })
  const [weeklyWindows, setWeeklyWindows] = useState<DoctorWeeklyAvailability[]>([])

  const [overrideForm, setOverrideForm] = useState({
    override_date: '',
    is_closed: false,
    start_time: '09:00',
    end_time: '17:00',
    note: '',
  })
  const [overrides, setOverrides] = useState<DoctorAvailabilityOverride[]>([])

  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' })
  const [isSavingPassword, setIsSavingPassword] = useState(false)
  const [sessionTimeout, setSessionTimeout] = useState('480')

  const savePassword = async () => {
    if (passwordForm.next !== passwordForm.confirm) {
      toast({ title: t('common.error'), description: t('settings.password_mismatch'), variant: 'destructive' })
      return
    }
    setIsSavingPassword(true)
    try {
      await authApi.updateProfile({ password: passwordForm.next, current_password: passwordForm.current } as any)
      toast({ title: t('settings.password_updated'), description: t('settings.password_updated_desc') })
      setPasswordForm({ current: '', next: '', confirm: '' })
    } catch (err: any) {
      toast({ title: t('common.error'), description: err?.response?.data?.detail || t('messages.error_saving'), variant: 'destructive' })
    } finally {
      setIsSavingPassword(false)
    }
  }

  const [professionalForm, setProfessionalForm] = useState({
    cedula_profesional: '',
    especialidad: '',
  })
  const [professionalSaveStatus, setProfessionalSaveStatus] = useState<SaveStatus>('idle')
  const lastSavedProfessional = useRef(professionalForm)
  useAutoResetStatus(professionalSaveStatus, setProfessionalSaveStatus)

  useEffect(() => {
    const next = {
      cedula_profesional: currentUser?.cedula_profesional || '',
      especialidad: currentUser?.especialidad || '',
    }
    setProfessionalForm(next)
    lastSavedProfessional.current = next
  }, [currentUser?.cedula_profesional, currentUser?.especialidad])

  const saveProfessionalData = async () => {
    if (
      professionalForm.cedula_profesional === lastSavedProfessional.current.cedula_profesional &&
      professionalForm.especialidad === lastSavedProfessional.current.especialidad
    ) {
      return
    }
    setProfessionalSaveStatus('saving')
    try {
      const updated = await authApi.updateProfile({
        cedula_profesional: professionalForm.cedula_profesional.trim() || undefined,
        especialidad: professionalForm.especialidad.trim() || undefined,
      })
      updateUser({ cedula_profesional: updated.cedula_profesional, especialidad: updated.especialidad })
      lastSavedProfessional.current = { ...professionalForm }
      setProfessionalSaveStatus('saved')
    } catch (err: any) {
      setProfessionalSaveStatus('error')
      toast({ title: t('common.error'), description: err?.response?.data?.detail || t('messages.error_saving'), variant: 'destructive' })
    }
  }

  const loadAvailabilityConfig = async (targetDoctorId: string) => {
    if (!targetDoctorId || !canManageAvailability) return
    setIsLoadingAvailability(true)
    try {
      try {
        const rule = await appointmentApi.getAvailabilityRule(targetDoctorId)
        const nextRule = {
          timezone: rule.timezone,
          slot_minutes: rule.slot_minutes,
          lead_time_minutes: rule.lead_time_minutes,
          max_horizon_days: rule.max_horizon_days,
          is_active: rule.is_active,
        }
        setRuleForm(nextRule)
        lastSavedRule.current = nextRule
      } catch (err: any) {
        if (err?.response?.status !== 404) throw err
        const fallbackRule = { timezone: 'UTC', slot_minutes: 30, lead_time_minutes: 120, max_horizon_days: 60, is_active: true }
        setRuleForm(fallbackRule)
        lastSavedRule.current = fallbackRule
      }
      const [weekly, overrideRows] = await Promise.all([
        appointmentApi.getWeeklyAvailability(targetDoctorId),
        appointmentApi.getAvailabilityOverrides(targetDoctorId),
      ])
      setWeeklyWindows(weekly.items)
      setOverrides(overrideRows.items)
    } catch {
      toast({ title: t('common.error'), description: t('settings.availability_load_error'), variant: 'destructive' })
    } finally {
      setIsLoadingAvailability(false)
    }
  }

  useEffect(() => {
    if (doctorId) loadAvailabilityConfig(doctorId)
  }, [doctorId])

  // `overrides` lets callers that fire synchronously (Select/Switch onChange, before setRuleForm's
  // update has flushed) pass the new value directly instead of reading the still-stale `ruleForm`
  // closure. Callers that fire after a re-render (MetricInput onBlur) can omit it and rely on state.
  const saveRule = async (overrides?: Partial<typeof ruleForm>) => {
    if (!doctorId) return
    const payload = overrides ? { ...ruleForm, ...overrides } : ruleForm
    if (!overrides && JSON.stringify(payload) === JSON.stringify(lastSavedRule.current)) return
    setRuleSaveStatus('saving')
    try {
      await appointmentApi.upsertAvailabilityRule(doctorId, payload)
      lastSavedRule.current = payload
      setRuleSaveStatus('saved')
      queryClient.invalidateQueries({ queryKey: ['availability-rule', doctorId] })
      loadAvailabilityConfig(doctorId)
    } catch (err: any) {
      setRuleSaveStatus('error')
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Error al guardar', variant: 'destructive' })
    }
  }

  const addWeeklyWindow = async () => {
    if (!doctorId) return
    try {
      await appointmentApi.createWeeklyAvailability({
        doctor_id: doctorId,
        weekday: weeklyForm.weekday,
        start_time: weeklyForm.start_time,
        end_time: weeklyForm.end_time,
        is_active: weeklyForm.is_active,
      })
      toast({ title: t('common.success'), description: t('settings.window_added') })
      loadAvailabilityConfig(doctorId)
    } catch (err: any) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Error al agregar', variant: 'destructive' })
    }
  }

  const deleteWeeklyWindow = async (id: string) => {
    if (!doctorId) return
    try {
      await appointmentApi.deleteWeeklyAvailability(id)
      loadAvailabilityConfig(doctorId)
    } catch {
      toast({ title: 'Error', description: 'Error al eliminar ventana semanal', variant: 'destructive' })
    }
  }

  const addOverride = async () => {
    if (!doctorId || !overrideForm.override_date) return
    try {
      await appointmentApi.createAvailabilityOverride({
        doctor_id: doctorId,
        override_date: overrideForm.override_date,
        is_closed: overrideForm.is_closed,
        start_time: overrideForm.is_closed ? undefined : overrideForm.start_time,
        end_time: overrideForm.is_closed ? undefined : overrideForm.end_time,
        note: overrideForm.note || undefined,
      })
      toast({ title: t('common.success'), description: t('settings.exception_added') })
      setOverrideForm((prev) => ({ ...prev, note: '' }))
      loadAvailabilityConfig(doctorId)
    } catch (err: any) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Error al agregar excepcion', variant: 'destructive' })
    }
  }

  const deleteOverride = async (id: string) => {
    if (!doctorId) return
    try {
      await appointmentApi.deleteAvailabilityOverride(id)
      loadAvailabilityConfig(doctorId)
    } catch {
      toast({ title: 'Error', description: 'Error al eliminar excepcion', variant: 'destructive' })
    }
  }

  const userInitials = `${currentUser?.first_name?.[0] || ''}${currentUser?.last_name?.[0] || ''}`.toUpperCase() || '?'
  const roleLabel = currentUser?.role?.replace('_', ' ') || ''

  return (
    <div className="animate-fade-in max-w-6xl mx-auto">
      {/* ── Row 1: Identity — Profile + Professional ── */}
      <div className="mb-6">
        <div className="bg-ml-surface-container-low rounded-2xl overflow-hidden">
            <div className="h-20 bg-gradient-to-br from-ml-primary/20 via-ml-primary/10 to-transparent" />
            <div className="px-8 pb-8">
              <div className="flex items-end gap-5 -mt-10 mb-8">
                <div className="h-16 w-16 rounded-2xl bg-ml-primary flex items-center justify-center shadow-lg shadow-ml-primary/20 ring-4 ring-ml-surface-container-low">
                  <span className="text-xl font-bold text-white font-body tracking-wide">{userInitials}</span>
                </div>
                <div className="pb-1">
                  <p className="text-base font-semibold text-ml-on-surface font-body leading-tight">
                    {`${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || '—'}
                  </p>
                  {roleLabel && (
                    <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded bg-ml-primary/10 text-ml-primary text-[10px] font-bold uppercase tracking-wider">
                      <BadgeCheck className="h-3 w-3" />
                      {roleLabel}
                    </span>
                  )}
                </div>
              </div>

              {/* Account info (read-only) */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-5 mb-8">
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/50">{t('settings.full_name')}</label>
                  <p className="text-sm text-ml-on-surface font-body font-medium py-2 border-b border-ml-outline-variant/20">
                    {`${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || '—'}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/50">{t('settings.email')}</label>
                  <p className="text-sm text-ml-on-surface font-body font-medium py-2 border-b border-ml-outline-variant/20 truncate">
                    {currentUser?.email || '—'}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/50">{t('settings.role')}</label>
                  <p className="text-sm text-ml-on-surface font-body font-medium py-2 border-b border-ml-outline-variant/20 capitalize">
                    {roleLabel || '—'}
                  </p>
                </div>
              </div>

              {/* Professional credentials (editable) */}
              <div className="border-t border-ml-outline-variant/10 pt-6">
                <div className="flex items-center gap-2 mb-5">
                  <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-ml-primary/10">
                    <ShieldCheck className="h-3.5 w-3.5 text-ml-primary" />
                  </div>
                  <h4 className="text-sm font-headline italic text-ml-primary flex-1">{t('settings.professional_data')}</h4>
                  <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-ml-primary/10 text-ml-primary/70">
                    NOM-004 · COFEPRIS
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/50">{t('settings.professional_license')}</label>
                    <Input
                      value={professionalForm.cedula_profesional}
                      onChange={(e) => setProfessionalForm(prev => ({ ...prev, cedula_profesional: e.target.value }))}
                      onBlur={saveProfessionalData}
                      placeholder="Ej. 12345678"
                      maxLength={20}
                      className="bg-transparent border-0 border-b border-ml-outline-variant/40 rounded-none px-0 focus-visible:ring-0 focus:border-ml-primary"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/50">{t('settings.specialty')}</label>
                    <Input
                      value={professionalForm.especialidad}
                      onChange={(e) => setProfessionalForm(prev => ({ ...prev, especialidad: e.target.value }))}
                      onBlur={saveProfessionalData}
                      placeholder="Ej. Medicina general"
                      maxLength={120}
                      className="bg-transparent border-0 border-b border-ml-outline-variant/40 rounded-none px-0 focus-visible:ring-0 focus:border-ml-primary"
                    />
                  </div>
                </div>
                <div className="mt-6 flex justify-end">
                  <SaveStatusIndicator status={professionalSaveStatus} />
                </div>
              </div>
            </div>
          </div>
      </div>

      {/* ── Row 2: System Preferences (full width, single column) ── */}
      <div className="bg-ml-surface-container-low p-6 rounded-2xl mb-10">
        <SectionHeader icon={Settings2} title={t('settings.system_preferences')} />
        <div className="divide-y divide-ml-outline-variant/10">
          <PrefRow
            icon={Globe}
            label={t('settings.language')}
            description={t('settings.language_desc')}
            control={<LanguageSelect className="w-36 bg-ml-surface-container-lowest border-ml-outline-variant/20 rounded-lg h-8 text-sm" />}
          />
        </div>

        {/* Theme by specialty — visual cards, live preview, dark mode & reset */}
        <div className="mt-6 border-t border-ml-outline-variant/10 pt-6">
          <SpecialtyThemePicker />
        </div>
      </div>

      {/* ── Row 3: Security ── */}
      <div className="bg-ml-surface-container-low p-6 rounded-2xl mb-10">
        <SectionHeader icon={KeyRound} title={t('settings.security')} />

        {/* Password change */}
        <div className="space-y-3 mb-6">
          <p className="text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/50">{t('settings.change_password')}</p>
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-ml-on-surface-variant/40">{t('settings.current_password')}</label>
            <Input
              type="password"
              value={passwordForm.current}
              onChange={(e) => setPasswordForm(p => ({ ...p, current: e.target.value }))}
              placeholder="••••••••"
              className="bg-transparent border-0 border-b border-ml-outline-variant/40 rounded-none px-0 focus-visible:ring-0 focus:border-ml-primary"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-ml-on-surface-variant/40">{t('settings.new_password')}</label>
              <Input
                type="password"
                value={passwordForm.next}
                onChange={(e) => setPasswordForm(p => ({ ...p, next: e.target.value }))}
                placeholder="••••••••"
                className="bg-transparent border-0 border-b border-ml-outline-variant/40 rounded-none px-0 focus-visible:ring-0 focus:border-ml-primary"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-ml-on-surface-variant/40">{t('settings.confirm_label')}</label>
              <Input
                type="password"
                value={passwordForm.confirm}
                onChange={(e) => setPasswordForm(p => ({ ...p, confirm: e.target.value }))}
                placeholder="••••••••"
                className="bg-transparent border-0 border-b border-ml-outline-variant/40 rounded-none px-0 focus-visible:ring-0 focus:border-ml-primary"
              />
            </div>
          </div>
          <div className="flex justify-end items-center gap-3 pt-1">
            {isSavingPassword && (
              <span className="inline-flex items-center gap-1.5 text-xs text-ml-on-surface-variant/50">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {t('settings.saving')}
              </span>
            )}
            <button
              type="button"
              onClick={savePassword}
              disabled={isSavingPassword || !passwordForm.current || !passwordForm.next}
              className="text-xs font-semibold text-ml-primary underline underline-offset-2 decoration-ml-primary/40 hover:decoration-ml-primary disabled:text-ml-on-surface-variant/30 disabled:no-underline disabled:cursor-not-allowed transition-colors"
            >
              {t('settings.update_password')}
            </button>
          </div>
        </div>

        {/* Session */}
        <div className="border-t border-ml-outline-variant/10 pt-5 divide-y divide-ml-outline-variant/10">
          <PrefRow
            icon={Timer}
            label={t('settings.auto_logout')}
            description={t('settings.auto_logout_desc')}
            control={
              <Select value={sessionTimeout} onValueChange={setSessionTimeout}>
                <SelectTrigger className="w-28 h-8 bg-ml-surface-container-lowest border-ml-outline-variant/20 rounded-lg text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="60">{t('settings.timeout_1h')}</SelectItem>
                  <SelectItem value="240">{t('settings.timeout_4h')}</SelectItem>
                  <SelectItem value="480">{t('settings.timeout_8h')}</SelectItem>
                  <SelectItem value="0">{t('settings.timeout_never')}</SelectItem>
                </SelectContent>
              </Select>
            }
          />
        </div>
      </div>

      {/* ── Row 4: Availability — Rules | Schedule + Exceptions ── */}
      {canManageAvailability && (
        <>
          <div className="flex items-center gap-4 mb-6">
            <span className="text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/40 whitespace-nowrap">
              {t('settings.availability_config')}
            </span>
            <div className="flex-1 h-px bg-ml-outline-variant/15" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">

          {/* Clinic rules */}
          <div className="bg-ml-surface-container-low p-6 rounded-2xl">
            <SectionHeader
              icon={Clock}
              title={t('settings.clinic_availability')}
              badge={isLoadingAvailability ? <Loader2 className="h-3.5 w-3.5 animate-spin text-ml-on-surface-variant/40" /> : undefined}
            />
            <div className="space-y-4">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/50 block mb-2">{t('settings.timezone')}</label>
                <Select
                  value={ruleForm.timezone}
                  onValueChange={(value) => {
                    setRuleForm(prev => ({ ...prev, timezone: value }))
                    saveRule({ timezone: value })
                  }}
                >
                  <SelectTrigger className="bg-ml-surface-container-lowest border border-ml-outline-variant/25 rounded-lg h-10 focus:ring-1 focus:ring-ml-primary/20 focus:border-ml-primary/40 transition-all">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timezoneOptions.map(tz => (
                      <SelectItem key={tz.value} value={tz.value}>{tz.country} ({tz.label})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <MetricInput
                label={t('settings.consult_duration')}
                value={ruleForm.slot_minutes}
                unit={t('settings.unit_min')}
                onChange={(n) => setRuleForm(prev => ({ ...prev, slot_minutes: n || 30 }))}
                onBlur={() => saveRule()}
              />
              <MetricInput
                label={t('settings.lead_time')}
                value={ruleForm.lead_time_minutes}
                unit={t('settings.unit_min')}
                onChange={(n) => setRuleForm(prev => ({ ...prev, lead_time_minutes: n }))}
                onBlur={() => saveRule()}
              />
              <MetricInput
                label={t('settings.max_horizon')}
                value={ruleForm.max_horizon_days}
                unit={t('settings.unit_days')}
                onChange={(n) => setRuleForm(prev => ({ ...prev, max_horizon_days: n || 1 }))}
                onBlur={() => saveRule()}
              />
            </div>
            <div className="mt-8 flex justify-end">
              <SaveStatusIndicator status={ruleSaveStatus} />
            </div>
          </div>

          {/* Weekly + Exceptions sub-grid (spans remaining 2 columns) */}
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">

          {/* Weekly windows */}
          <div className="bg-ml-surface-container-low p-6 rounded-2xl">
            <SectionHeader icon={CalendarCheck} title={t('settings.weekly_windows')} />

            {/* Existing windows */}
            <div className="space-y-1.5 mb-5">
              {weeklyWindows.length === 0 ? (
                <div className="py-5 text-center flex flex-col items-center gap-1.5">
                  <CalendarCheck className="h-5 w-5 text-ml-on-surface-variant/20" />
                  <p className="text-sm text-ml-on-surface-variant/40 font-body italic">{t('settings.no_weekly_windows')}</p>
                  <p className="text-xs text-ml-on-surface-variant/30">{t('settings.add_window_hint')}</p>
                </div>
              ) : (
                weeklyWindows.map((row) => (
                  <div key={row.id} className="flex items-center gap-2.5 group">
                    <div className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg border border-ml-outline-variant/20 bg-ml-surface-container-lowest text-[11px] font-semibold text-ml-on-surface-variant tracking-wide">
                      {t(`settings.weekday_short_${row.weekday}`)}
                    </div>
                      <div className="flex-1 bg-ml-surface-container-lowest border border-ml-outline-variant/10 h-9 px-3 rounded-lg flex items-center gap-2 min-w-0">
                        <span className="text-sm font-mono text-ml-on-surface tabular-nums">{row.start_time}</span>
                        <span className="text-ml-on-surface-variant/25 text-xs mx-0.5">–</span>
                        <span className="text-sm font-mono text-ml-on-surface tabular-nums">{row.end_time}</span>
                      </div>
                      <button
                        onClick={() => deleteWeeklyWindow(row.id)}
                        className="opacity-0 group-hover:opacity-100 transition-all h-9 w-9 flex items-center justify-center shrink-0 rounded-lg text-ml-on-surface-variant/40 hover:text-ml-error hover:bg-ml-error/5"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )
                )
              )}
            </div>

            {/* Add window form */}
            <div className="border-t border-ml-outline-variant/10 pt-4 space-y-3">
              {/* Day chip picker */}
              <div className="flex gap-1">
                {weekdayOptions.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setWeeklyForm(prev => ({ ...prev, weekday: opt.value }))}
                    className={cn(
                      'flex-1 h-8 rounded-lg border text-[11px] font-semibold transition-all',
                      weeklyForm.weekday === opt.value
                        ? 'bg-ml-primary text-white border-ml-primary'
                        : 'bg-ml-surface-container-lowest border-ml-outline-variant/20 text-ml-on-surface-variant/50 hover:border-ml-outline-variant/40 hover:text-ml-on-surface-variant',
                    )}
                  >
                    {t(`settings.weekday_short_${opt.value}`)}
                  </button>
                ))}
              </div>
              {/* Time range */}
              <div className="flex items-center gap-2">
                <Input
                  type="time"
                  value={weeklyForm.start_time}
                  onChange={(e) => setWeeklyForm(prev => ({ ...prev, start_time: e.target.value }))}
                  className="h-9 border-ml-outline-variant/20 text-sm bg-ml-surface-container-lowest rounded-lg focus:ring-1 focus:ring-ml-primary/30 flex-1 min-w-0"
                />
                <span className="text-ml-on-surface-variant/30 text-xs shrink-0">—</span>
                <Input
                  type="time"
                  value={weeklyForm.end_time}
                  onChange={(e) => setWeeklyForm(prev => ({ ...prev, end_time: e.target.value }))}
                  className="h-9 border-ml-outline-variant/20 text-sm bg-ml-surface-container-lowest rounded-lg focus:ring-1 focus:ring-ml-primary/30 flex-1 min-w-0"
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={addWeeklyWindow}
                  disabled={!doctorId}
                  className="text-xs font-semibold text-ml-primary underline underline-offset-2 decoration-ml-primary/40 hover:decoration-ml-primary disabled:text-ml-on-surface-variant/30 disabled:no-underline disabled:cursor-not-allowed transition-colors"
                >
                  + {t('settings.add_window')}
                </button>
              </div>
            </div>
          </div>

          {/* Date exceptions */}
          <div className="bg-ml-surface-container-low p-6 rounded-2xl">
            <SectionHeader icon={CalendarCheck} title={t('settings.date_exceptions')} />

            {/* Add exception form */}
            <div className="mb-5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/50 mb-3">{t('settings.new_exception_label')}</p>
              <div className="flex items-center gap-2">
                <Input
                  id="override-date-input"
                  type="date"
                  value={overrideForm.override_date}
                  onChange={(e) => setOverrideForm(prev => ({ ...prev, override_date: e.target.value }))}
                  className="h-9 border-ml-outline-variant/20 text-sm bg-ml-surface-container-lowest rounded-lg focus:ring-1 focus:ring-ml-primary/30 flex-1"
                />
              </div>
              <div className="flex justify-end mt-2">
                <button
                  type="button"
                  onClick={addOverride}
                  disabled={!doctorId || !overrideForm.override_date}
                  className="text-xs font-semibold text-ml-primary underline underline-offset-2 decoration-ml-primary/40 hover:decoration-ml-primary disabled:text-ml-on-surface-variant/30 disabled:no-underline disabled:cursor-not-allowed transition-colors"
                >
                  + {t('settings.exception')}
                </button>
              </div>
            </div>

            {/* Exceptions list */}
            <div className="border-t border-ml-outline-variant/10 pt-4">
              {overrides.length === 0 ? (
                <div className="py-8 text-center flex flex-col items-center gap-2">
                  <div className="h-10 w-10 rounded-xl bg-ml-outline-variant/10 flex items-center justify-center">
                    <CalendarCheck className="h-5 w-5 text-ml-on-surface-variant/25" />
                  </div>
                  <p className="text-sm text-ml-on-surface-variant/40 font-body italic">{t('settings.no_exceptions')}</p>
                  <p className="text-xs text-ml-on-surface-variant/30">{t('settings.no_exceptions_hint')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {overrides.map((row) => (
                    <div key={row.id} className="flex items-center justify-between rounded-xl px-4 py-3 bg-ml-surface-container-lowest group hover:shadow-sm transition-all">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-sm font-semibold text-ml-on-surface font-body">{row.override_date}</span>
                        {row.is_closed ? (
                          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-ml-error/10 text-ml-error">{t('settings.closed')}</span>
                        ) : (
                          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-ml-primary/10 text-ml-primary">
                            {row.start_time} - {row.end_time}
                          </span>
                        )}
                        {row.note && <span className="text-xs text-ml-on-surface-variant/40 italic">{row.note}</span>}
                      </div>
                      <button
                        onClick={() => deleteOverride(row.id)}
                        className="opacity-0 group-hover:opacity-100 transition-all p-1.5 rounded text-ml-on-surface-variant/40 hover:text-ml-error hover:bg-ml-error/5 ml-2"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          </div>{/* end sub-grid */}

        </div>{/* end outer availability grid */}
        </>
      )}

      {/* Footer */}
      <footer className="mt-16 pt-6 border-t border-ml-outline-variant/10 flex justify-between items-center">
        <button className="text-xs font-bold text-ml-on-surface-variant/40 uppercase tracking-widest hover:text-ml-error transition-colors">
          {t('settings.discard_changes')}
        </button>
        <button
          onClick={() => saveRule()}
          className="text-xs font-bold text-ml-primary uppercase tracking-widest underline underline-offset-2 decoration-ml-primary/40 hover:decoration-ml-primary transition-colors"
        >
          {t('settings.save_changes')}
        </button>
      </footer>
    </div>
  )
}
